import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isMasterclassPath } from '../data/masterclass';
import { OFFICES, ICON } from '../config/contact';
import AppDownload from './AppDownload';
import { FOOTER_OUTCOMES, FOOTER_TAGLINE } from '../data/homeOutcomes';
import './Footer.css';

// The services, in the header's order (also in scripts/prerender.js SITE_LINKS).
const SERVICES = [
  // All three on one page (owner 2026-11-05).
  { label: 'All Services', path: '/services' },
  { label: 'Managed Follow-Ups', path: '/managed-follow-ups' },
  { label: 'Dental SEO', path: '/dental-seo' },
  { label: 'Dental Websites', path: '/clinic-websites' },
  { label: 'Dental Ads (Meta & Google)', path: '/dental-ads' },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();
  // Owner 2026-10-22: the app badges are hidden on the home page only; every other page keeps them.
  const { pathname } = useLocation();
  const showApps = pathname !== '/';

  // Contact offices come from the shared config: US build shows the US office
  // only; India build shows both India and US.
  const multiOffice = OFFICES.length > 1;

  // "What we fix" (owner 2026-10-22, outcomes as a service): the module pages, linked in outcome words
  // with no product names, so every module page keeps a link from every page. Words: src/data/homeOutcomes.js.
  const outcomes = FOOTER_OUTCOMES;

  // Dental-growth pages only. The legacy engineering/consulting pages (About,
  // Services, Case Studies, Insights, Engineering Partner) keep their routes
  // but are intentionally not linked anywhere on the Aumy site.
  const company = [
    { label: 'Aumy — How It Works', path: '/revenue-generator' },
    { label: 'Modules', path: '/modules' },
    { label: 'AI Dental Software India', path: '/ai-dental-software-india' },
    { label: 'AI Receptionist', path: '/ai-receptionist' },
    { label: 'WhatsApp Automation', path: '/whatsapp-automation-for-clinics' },
    { label: 'Switching Dental Software?', path: '/switch' },
    { label: 'Watch Demo Videos', path: '/demos' },
    { label: 'Dental Practice Growth Hub', path: '/growth' },
    { label: 'Podcast — Be a Guest', path: '/podcast' },
    { label: 'Facebook & Instagram Receptionist', path: '/facebook-instagram' },
    // No "free" on the masterclass pages (owner 2026-10-29).
    { label: isMasterclassPath(pathname) ? 'Clinic Audit' : 'Free Clinic Audit', path: '/growth-audit' },
    { label: 'Masterclass — The Full Chair Method', path: '/masterclass' },
    { label: 'What Slips Through the Cracks — 60-second Check', path: '/leak-calculator' },
    { label: 'Missed Call Calculator', path: '/missed-call-calculator' },
    { label: 'Platform Partners', path: '/platform-partner' },
    { label: 'Contact', path: '/contact' }
  ];

  const legal = [
    { label: 'Privacy Policy', path: '/privacy' },
    { label: 'Compliance', path: '/compliance' },
    { label: 'Terms of Service', path: '/terms' },
    { label: 'Shipping Policy', path: '/shipping' },
    { label: 'Cancellation & Refund', path: '/refund' }
  ];

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-main">
          {/* Brand Column */}
          <div className="footer-brand">
            <Link to="/" className="footer-logo">
              <img src="/aumy-waves-lockup-dark.png" alt="Aumy by AUM AI" />
            </Link>
            <p className="footer-byline">an AUM AI product</p>
            {/* Aumy app on both stores (owner ask, 8 Sept 2026) */}
            {showApps && (
              <div className="footer-apps">
                <p className="footer-apps-label">Get the Aumy app</p>
                <AppDownload variant="compact" />
              </div>
            )}
            <p className="footer-tagline">{FOOTER_TAGLINE}</p>
            <div className="footer-social">
              <a href="https://www.linkedin.com/in/chaudhari-jayesh-b9762a3b/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* What we fix: outcome links to the module pages */}
          <div className="footer-column">
            <h4>What we fix</h4>
            <ul>
              {outcomes.map((o) => (
                <li key={o.to}>
                  <Link to={o.to}>{o.label}</Link>
                </li>
              ))}
            </ul>
            {/* Services (owner 2026-10-23), as under "Product & services" in the header. */}
            <h4 className="footer-subhead">Services</h4>
            <ul>
              {SERVICES.map((s) => (
                <li key={s.path}>
                  <Link to={s.path}>{s.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div className="footer-column">
            <h4>Company</h4>
            <ul>
              {company.map((item, index) => (
                <li key={index}>
                  <Link to={item.path}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div className="footer-column">
            <h4>Contact</h4>
            <div className="footer-contact">
              {OFFICES.map((office) => (
                <div className="footer-office" key={office.key}>
                  {multiOffice && <p className="footer-office-name">{office.label}</p>}
                  <div className="contact-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d={ICON.mail} />
                    </svg>
                    <a href={`mailto:${office.email}`}>{office.email}</a>
                  </div>
                  <div className="contact-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d={ICON.phone} />
                    </svg>
                    <a href={office.phoneTel}>{office.phone}</a>
                  </div>
                  <div className="contact-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d={ICON.pin} />
                    </svg>
                    <span>{office.entity} · {office.address}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <p>&copy; {currentYear} AUM AI Healthcare Solutions. All rights reserved. Aumy™ is a trademark of AAUMAI Healthcare Solution Pvt Ltd.</p>
          <div className="footer-legal">
            {legal.map((item, index) => (
              <Link key={index} to={item.path}>{item.label}</Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
